/**
 * InfraStrategyCompare — V105-5-5, the strategy comparison after the verdict board in a FedRAMP /
 * GovRAMP program's Scope tab (`?view=strategies`), ADR-0072 §6 (D-105-3, owner decisions D1–D3).
 *
 *   - A matrix: one column per strategy (hybrid, one government cloud, inherit a boundary, stay
 *     commercial — always shown, the Baseline); a Target row for the single-target strategies
 *     (their candidates, one Recommended, D1); five fixed rows (fits the level, left open, controls
 *     gained, code to change, cost). "Best" marks a value strictly better than another column's
 *     with no unknowns in it. A fully unknown cell is one "Not known" link; a partly unknown one
 *     reads "3 + 5 not known". Each unknown and each fit status filters the services behind it.
 *   - The viewed strategy lists its services (still open first, then not known, resolved
 *     collapsed). Every reason the scorer gives has plain words, and every problem has its action
 *     or the "Waiting on GAC Pro review" tag (REASONS; a test checks the scorer's code list).
 *   - ONE primary action in the head row: Answer them → Confirm (stale) / Choose → Open the target plan
 *     (V105-6-7; Back to Scope when no plan view is wired).
 *   - Swaps live in the URL (family, target, swap) so a lead can send them to an admin.
 *   - Labels come from registry / catalog names and counts only; no LLM text, ids or codes.
 *
 * Reads: any member. Swaps and cost estimates: owner / admin or the program lead (server
 * `can_write`). Choosing: owner / admin only (server `can_decide`); the server signs the record.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Select, Spinner, Textarea, Toast } from '@aigovops/design-system';
import type { CostBand, StrategyComparison, StrategyFamily, StrategyVector } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { chooseStrategy, estimateCost, getStrategies, getStrategyDecision, listStrategyDecisions, scoreStrategy } from '../../lib/infra-plan-api';
import { programLabel } from '../../lib/boundary-model';
import { fmtDate } from '../../lib/format';
import { AskAdmin } from '../AskAdmin';
import { LEVEL_LABEL, VERDICT_LABEL, label } from './InfraVerdictBoard';
import type { ProgramToast, ToastApi } from './program-toast';
import './infra-inventory.css';
import './verdict-board.css';
import './strategy-compare.css';

type Scored = StrategyComparison['strategies'][number];
type ItemOptions = StrategyComparison['items'][number];
type Line = StrategyVector['items'][number];

export const FAMILY_LABEL: Record<StrategyFamily, string> = {
  hybrid: 'Hybrid', single_hyperscaler: 'One government cloud', inherit_boundary: 'Inherit a boundary', stay_commercial: 'Stay commercial',
};
const FAMILY_HINT: Record<StrategyFamily, string> = {
  hybrid: 'Keep the platform; move each piece to an authorized service',
  single_hyperscaler: 'Move to one cloud’s government region',
  inherit_boundary: 'Run inside another company’s authorized boundary',
  stay_commercial: 'Change nothing; document every third party',
};
export const TARGET_LABEL: Record<string, string> = {
  aws: 'AWS GovCloud', azure: 'Azure Government', gcp: 'Google Cloud (Assured Workloads)', fedstart: 'Palantir FedStart', knox: 'Knox', game_warden: 'Game Warden',
};
export const COST_LABEL: Record<CostBand, string> = { low: 'Low', medium: 'Medium', high: 'High', very_high: 'Very high' };
const FAMILIES: StrategyFamily[] = ['hybrid', 'single_hyperscaler', 'inherit_boundary', 'stay_commercial'];
const SUPPORT_EMAIL = 'support@gacpro.io';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const keyOf = (family: StrategyFamily, anchor: string | null | undefined) => `${family}:${anchor ?? ''}`;
const targetName = (t: string | null | undefined) => (t ? TARGET_LABEL[t] ?? t : null);
/** "Azure Government" for a single-target strategy, else the family name. */
export const strategyName = (family: StrategyFamily, anchor: string | null | undefined) => targetName(anchor) ?? FAMILY_LABEL[family];
const fullName = (family: StrategyFamily, anchor: string | null | undefined) => `${FAMILY_LABEL[family]}${anchor ? ` · ${targetName(anchor)}` : ''}`;

/** Status in plain words (class fit). */
export function fitText(v: StrategyVector, level: string): { text: string; tone: 'ok' | 'bad' | 'warn' } {
  if (v.class_fit.status === 'pass') return { text: `Fits ${level}`, tone: 'ok' };
  if (v.class_fit.status === 'fail') return { text: 'Doesn’t fit yet', tone: 'bad' };
  return { text: 'Not known yet', tone: 'warn' };
}

/** One sentence for the live region. */
export function impactLines(v: StrategyVector): string[] {
  const open = v.verdicts_resolved.unresolved_item_ids.length;
  return [open ? `${plural(open, 'service')} left open` : 'Nothing left open', `${plural(v.inheritance_gained.count, 'control objective')} from providers`, `${plural(v.code_impact.call_sites, 'code place')} to change`];
}

/** Why a candidate leads (the scorer's lead reason), in plain words. */
export function leadText(reason: string | undefined, unknownCount = 0): string {
  const [code, n] = (reason ?? '').split(':');
  switch (code) {
    case 'FITS': return n === 'unknown' ? `The others don’t fit; this one may, once ${plural(unknownCount, 'answer')} ${unknownCount === 1 ? 'is' : 'are'} in` : 'Fits the level where the others don’t';
    case 'FEWER_OPEN': return 'Leaves the fewest services open';
    case 'FEWER_UNKNOWN': return 'Has the fewest unknowns';
    case 'MORE_INHERITANCE': return 'Gains the most controls from providers';
    case 'LESS_CODE': return `Changes the least code (${plural(Number(n) || 0, 'place')})`;
    case 'FEWER_UNSCANNED': return 'Has the fewest unscanned services';
    case 'LOWER_COST': return 'Costs the least';
    case 'ONLY_CANDIDATE': return 'The only option';
    default: return 'Even with the others; listed first';
  }
}

/** The first matrix row that separates two candidates, as "<row>: a vs b" (display order). */
export function separatingRow(a: StrategyVector, b: StrategyVector, level: string): string | null {
  if (a.class_fit.status !== b.class_fit.status) return `Fits the level: ${fitText(a, level).text} vs ${fitText(b, level).text}`;
  const pairs: Array<[string, number, number]> = [
    ['Left open', a.verdicts_resolved.unresolved_item_ids.length, b.verdicts_resolved.unresolved_item_ids.length],
    ['Not known', a.verdicts_resolved.unknown_item_ids.length, b.verdicts_resolved.unknown_item_ids.length],
    ['Controls gained', a.inheritance_gained.count, b.inheritance_gained.count],
    ['Code to change', a.code_impact.call_sites, b.code_impact.call_sites],
  ];
  const hit = pairs.find(([, x, y]) => x !== y);
  return hit ? `${hit[0]}: ${hit[1]} vs ${hit[2]}` : null;
}

// ---- every reason code: plain words + the action that addresses it (R2-H5) ------------------------------

export type ReasonAction = 'none' | 'scan' | 'coverage' | 'estimate' | 'gacpro' | 'pick' | 'target';
export const REASONS: Record<string, { words: ((rest: string) => string) | null; action: ReasonAction | ((rest: string) => ReasonAction) }> = {
  VERDICT: { words: null, action: 'none' },
  KEEP: { words: null, action: 'none' },
  CATALOG: { words: null, action: 'none' },
  TARGET_OFFERING: { words: null, action: 'none' },
  KEEP_REPLACE_UNRESOLVED: { words: () => 'It still needs a replacement', action: 'pick' },
  KEEP_PARTITION_NOT_APPLIED: { words: () => 'Federal data still reaches it', action: 'pick' },
  PARTITION_PATTERN: { words: () => 'Federal data is kept off it', action: 'none' },
  OFFERING_MOVE: { words: () => 'Moves to the provider’s authorized offering', action: 'none' },
  COVERED_AT: { words: () => 'Covered at the program’s level', action: 'none' },
  NOT_COVERED_AT_LEVEL: { words: () => 'The provider’s authorized offering doesn’t cover it at this level', action: 'coverage' },
  CATALOG_ROW_MISSING: { words: () => 'This option is no longer in the catalog', action: 'pick' },
  OPTION_NOT_IN_CATALOG: { words: () => 'This option is no longer in the catalog', action: 'pick' },
  CATALOG_UNVERIFIED: { words: () => 'This option is not verified yet', action: 'gacpro' },
  TARGET_OFFERING_ABSTAINED: { words: () => 'The target’s authorization is not known yet', action: 'gacpro' },
  TARGET_AT_LEVEL: { words: () => 'The target is authorized at the program’s level', action: 'none' },
  TARGET_BELOW_LEVEL: { words: () => 'The target is authorized below the program’s level', action: 'target' },
  TARGET_LEVEL_UNKNOWN: { words: () => 'The target’s authorization level is not known yet', action: 'gacpro' },
  TARGET_COVERAGE: {
    words: (r) => (r === 'covered' ? 'The target’s authorization covers it' : r === 'not_covered' ? 'The target’s authorization does not cover it' : 'Whether the target’s authorization covers it is not confirmed'),
    action: (r) => (r === 'covered' ? 'none' : r === 'not_covered' ? 'pick' : 'gacpro'),
  },
  TARGET_PRODUCT_ABSTAINED: { words: () => 'Which product it moves to is not confirmed yet', action: 'gacpro' },
  CODE_CHANGE_NONE: { words: () => 'No code change', action: 'none' },
  CALL_SITES: { words: (r) => `${plural(Number(r.split('@')[0]) || 0, 'code place')} use it`, action: 'none' },
  CALL_SITES_UNKNOWN: { words: () => 'Not found by a repository scan yet', action: 'scan' },
  COST_BAND: { words: (r) => `Cost ${COST_LABEL[r as CostBand]?.toLowerCase() ?? 'set'}`, action: 'none' },
  COST_BAND_ESTIMATE: { words: (r) => `Cost ${COST_LABEL[r as CostBand]?.toLowerCase() ?? 'set'} (your estimate)`, action: 'none' },
  COST_BAND_ABSTAINED: { words: () => 'Cost not known yet', action: 'estimate' },
  COST_BAND_NO_CATALOG_ROW: { words: () => 'Cost not known yet', action: 'estimate' },
  INHERITANCE: { words: () => 'Gains controls from the provider', action: 'none' },
  INHERITANCE_UNKNOWN: { words: () => 'Controls from the provider not known yet', action: 'gacpro' },
  NOT_HOSTING_NO_INHERITANCE: { words: () => 'Reached over an API: documented, not inherited', action: 'none' },
};
const split = (r: string) => { const i = r.indexOf(':'); return i < 0 ? [r, ''] as const : [r.slice(0, i), r.slice(i + 1)] as const; };

/** "Why" for one service line (plain words; codes never shown). */
export function itemWhy(reasons: readonly string[]): string[] {
  const out: string[] = [];
  for (const r of reasons) {
    const [code, rest] = split(r);
    const w = REASONS[code]?.words;
    if (w) out.push(w(rest));
  }
  return [...new Set(out)];
}
/** The actions a line's reasons call for (deduped; GAC Pro gaps become the tag). */
export function itemActions(reasons: readonly string[]): ReasonAction[] {
  const out = new Set<ReasonAction>();
  for (const r of reasons) {
    const [code, rest] = split(r);
    const a = REASONS[code]?.action;
    const v = typeof a === 'function' ? a(rest) : a;
    if (v && v !== 'none') out.add(v);
  }
  return [...out];
}

/** class_fit reasons in plain words (item-level ones filter rows; VVK_* are workspace-level). */
export const FIT_WORDS: Record<string, string> = {
  VERDICT_PENDING: 'a service still needs answers', RESOLUTION_UNKNOWN: 'whether a service is resolved is not known', REPLACE_UNRESOLVED: 'a service still needs a replacement',
  PARTITION_UNRESOLVED: 'federal data still reaches a service', TARGET_BELOW_LEVEL: 'a target is authorized below the level', TARGET_NOT_COVERED: 'a target’s authorization does not cover a service',
  VVK_TARGET_EVIDENCE_UNKNOWN: 'automated evidence after a move is not known yet', VVK_RULES_MISSING: 'the automated-evidence minimum is not published yet',
  VVK_NO_AUTOMATED_EVIDENCE: 'no KSI has automated evidence yet', VVK_BELOW_MINIMUM: 'KSIs are below the class minimum for automated evidence',
};
const WORKSPACE_FIT = new Set(['VVK_RULES_MISSING', 'VVK_NO_AUTOMATED_EVIDENCE', 'VVK_BELOW_MINIMUM']);

const costUnknown = (l: Line) => l.cost_band === 'unknown';
export type UnknownFilter = 'resolution' | 'inheritance' | 'code' | 'cost' | 'fit';
const FILTER_TEXT: Record<UnknownFilter, string> = {
  resolution: 'whether it resolves the service is not known', inheritance: 'controls from the provider are not known', code: 'not found by a repository scan yet',
  cost: 'cost not known yet', fit: 'the fit is decided',
};
const matches = (v: StrategyVector, l: Line, f: UnknownFilter) => {
  if (f === 'resolution') return v.verdicts_resolved.unknown_item_ids.includes(l.item_id);
  if (f === 'inheritance') return v.inheritance_gained.unknown_item_ids.includes(l.item_id);
  if (f === 'code') return v.code_impact.unknown_item_ids.includes(l.item_id);
  if (f === 'cost') return costUnknown(l);
  return v.class_fit.reasons.some((r) => r.item_id === l.item_id);
};

/** URL state (H4): `family`, `target`, `swap=item:option,…` (each part URI-encoded). */
export function parseSwaps(raw: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of (raw ?? '').split(',').filter(Boolean)) {
    const i = part.indexOf(':');
    if (i > 0) out[decodeURIComponent(part.slice(0, i))] = decodeURIComponent(part.slice(i + 1));
  }
  return out;
}
export const formatSwaps = (sw: Record<string, string>) => Object.entries(sw).sort(([a], [b]) => (a < b ? -1 : 1)).map(([k, v]) => `${encodeURIComponent(k)}:${encodeURIComponent(v)}`).join(',');

// ---- best-value marking (R2-H8): strictly better than at least one other column, no unknowns ------------

type CellVal = { n: number; unknown: number } | null;
export function bestColumns(vals: CellVal[], better: 'min' | 'max'): boolean[] {
  const known = vals.map((v) => (v && v.unknown === 0 ? v.n : null));
  const nums = known.filter((x): x is number => x !== null);
  if (nums.length < 2 && vals.filter(Boolean).length < 2) return vals.map(() => false);
  const target = better === 'min' ? Math.min(...nums) : Math.max(...nums);
  // Higher is better: a partly unknown cell whose known part already reaches the best known value could be higher.
  if (better === 'max' && vals.some((v) => v && v.unknown > 0 && v.n >= target)) return vals.map(() => false);
  const all = vals.filter(Boolean).map((v) => v!.n);
  const someWorse = all.some((x) => (better === 'min' ? x > target : x < target));
  return known.map((x) => x !== null && x === target && someWorse);
}

const errBody = (e: unknown) => (e instanceof ApiError ? (e.body as { code?: string; error?: string; coverage_conflict_item_ids?: string[] } | undefined) : undefined);
/** Owner decision 2026-10-09: the board action that settles a coverage conflict either way. */
const COVERAGE_ACTION = 'Say whether it’s covered';
const errMsg = (e: unknown, fallback: string) => {
  if (e instanceof ApiError && e.status === 403) return 'Only an owner or an admin can do that.';
  return e instanceof Error && e.message ? e.message : fallback;
};
interface ToastState extends ProgramToast { version: number }
type ErrorState = { text: string; tone?: 'error' | 'warning'; support?: boolean; retry?: () => void; inventory?: boolean; /** A coverage-conflict item to settle on the board. */ coverage?: string };
type FocusTarget = 'chosen' | 'answer' | 'choose' | 'detail' | 'heading' | 'vvk' | 'reason' | `est:${string}` | `opt:${string}` | `target:${string}`;

export function InfraStrategyCompare({ slug, prefix, program, onBack, onOpenInventory, onBackToScope, onOpenPlan, toastApi }: {
  slug: string; prefix: string; program: string;
  /** Back to the verdict board; `open` deep-links one row's panel (R2-H4). */
  onBack: (open?: { itemId: string; panel: 'coverage' }) => void;
  /** The inventory review (repository scans live there). */
  onOpenInventory?: () => void;
  /** The program's Scope tab (the done state's way back). */
  onBackToScope?: () => void;
  /** V105-6-7: the target plan (the done state's next step, when wired). */
  onOpenPlan?: () => void;
  toastApi?: ToastApi;
}) {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const key = useMemo(() => ['infra-plan', slug, 'strategies', program] as const, [slug, program]);
  const q = useQuery({ queryKey: key, queryFn: () => getStrategies(slug, program) });
  const urlFamily = (FAMILIES as string[]).includes(sp.get('family') ?? '') ? (sp.get('family') as StrategyFamily) : null;
  const [viewing, setViewing] = useState<StrategyFamily | null>(urlFamily);
  const [targets, setTargets] = useState<Partial<Record<StrategyFamily, string | null>>>(() => (urlFamily && sp.get('target') ? { [urlFamily]: sp.get('target') } : {}));
  const [swaps, setSwaps] = useState<Record<string, Record<string, string>>>({});
  const [pending, setPending] = useState<Record<string, string>>({});
  const [customs, setCustoms] = useState<Record<string, Scored>>({});
  const [filter, setFilter] = useState<UnknownFilter | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [rationale, setRationale] = useState('');
  const [fieldErr, setFieldErr] = useState<string | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [live, setLive] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [estimating, setEstimating] = useState<string | null>(null);
  const [scrollable, setScrollable] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const detailH = useRef<HTMLHeadingElement>(null);
  const chosenRef = useRef<HTMLDivElement>(null);
  const vvkRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef('');
  const busyRef = useRef<string | null>(null);
  const pendingRef = useRef<Record<string, Record<string, string>>>({});
  const staleRescored = useRef<string | null>(null);
  const [awaitScore, setAwaitScore] = useState<string | null>(null);
  const chooseBtn = useRef<HTMLButtonElement>(null);
  const answerBtn = useRef<HTMLButtonElement>(null);
  const reason = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);
  const version = useRef(0);
  const focusNext = useRef<FocusTarget | null>(null);
  const hydrated = useRef(false);
  reasonRef.current = rationale;
  busyRef.current = busy;
  const show = (t: ProgramToast) => {
    if (toastApi) { toastApi.show({ ...t, returnFocus: t.returnFocus ?? heading.current }); return; }
    version.current += 1; setToast({ ...t, version: version.current });
  };
  const decisionsQ = useQuery({ queryKey: ['infra-plan', slug, 'strategy-decisions'], queryFn: () => listStrategyDecisions(slug), enabled: !!q.data?.comparison.latest_decision });

  const focusedOnce = useRef(false);
  useEffect(() => { if (!focusedOnce.current && q.data && heading.current) { focusedOnce.current = true; heading.current.focus(); } });
  useEffect(() => { if (choosing) reason.current?.focus(); }, [choosing]);
  // H9 / R2-H9: focus after a state change, once the target renders; fall back to the heading.
  useEffect(() => {
    const f = focusNext.current;
    if (!f) return;
    let el: HTMLElement | null = null;
    if (f === 'chosen') el = chosenRef.current;
    else if (f === 'answer') el = answerBtn.current;
    else if (f === 'choose') el = chooseBtn.current;
    else if (f === 'detail') el = detailH.current;
    else if (f === 'heading') el = heading.current;
    else if (f === 'vvk') el = vvkRef.current ?? detailH.current;
    else if (f === 'reason') el = reason.current;
    else if (f.startsWith('target:')) el = root.current?.querySelector<HTMLElement>(`[id="sc-target-${f.slice(7)}"]`) ?? null;
    else if (f.startsWith('est:')) el = [...(root.current?.querySelectorAll<HTMLElement>('[data-est-btn]') ?? [])].find((x) => x.dataset.estBtn === f.slice(4)) ?? null;
    else if (f.startsWith('opt:')) el = [...(root.current?.querySelectorAll<HTMLElement>('select[id^="sc-opt-"]') ?? [])].find((x) => x.id === `sc-opt-${f.slice(4)}`) ?? null;
    focusNext.current = null;
    (el ?? heading.current)?.focus();
  });
  useLayoutEffect(() => {
    const w = wrap.current;
    if (w) { const s = w.scrollWidth > w.clientWidth + 1; if (s !== scrollable) setScrollable(s); }
  });

  const data = q.data;
  const cmp = data?.comparison;
  const decision = cmp?.latest_decision ?? null;
  const stale = !!cmp?.latest_decision_stale;
  const leaderFamily = (cmp?.leader?.family ?? cmp?.strategies[0]?.instance.family ?? 'stay_commercial') as StrategyFamily;
  const fam: StrategyFamily = viewing ?? (decision && FAMILIES.includes(decision.family) ? decision.family : leaderFamily);
  const group = (f: StrategyFamily) => cmp?.groups.find((g) => g.family === f) ?? null;
  const anchorFor = (f: StrategyFamily): string | null => {
    const g = group(f);
    if (!g || !g.candidates.length) return null;
    const want = targets[f] ?? (decision && decision.family === f ? decision.anchor : undefined);
    const hit = g.candidates.find((c) => (c.instance.anchor ?? null) === (want ?? null));
    return (hit ?? g.candidates[0]!).instance.anchor ?? null;
  };
  const candidateAt = (f: StrategyFamily, a: string | null): Scored | null => group(f)?.candidates.find((c) => (c.instance.anchor ?? null) === a) ?? group(f)?.candidates[0] ?? null;
  const candidate = (f: StrategyFamily) => candidateAt(f, anchorFor(f));
  const isChosen = (f: StrategyFamily, a: string | null) => !!decision && decision.family === f && (decision.anchor ?? null) === a;
  const signedSwaps = (f: StrategyFamily, a: string | null): Record<string, string> => {
    const base = candidateAt(f, a);
    if (!decision || !base || !isChosen(f, a) || decision.generated) return {};
    const b = new Map(base.instance.choices.map((c) => [c.item_id, c.option]));
    return Object.fromEntries(decision.choices.filter((c) => b.get(c.item_id) !== c.option).map((c) => [c.item_id, c.option]));
  };
  const swapsAt = (f: StrategyFamily, a: string | null) => swaps[keyOf(f, a)] ?? signedSwaps(f, a);
  const vectorAt = (f: StrategyFamily, a: string | null): StrategyVector | null => {
    const k = keyOf(f, a);
    if (customs[k]) return customs[k]!.vector;
    if (isChosen(f, a) && !swaps[k] && !stale && cmp?.latest_decision_vector) return cmp.latest_decision_vector;
    return candidateAt(f, a)?.vector ?? null;
  };
  const vectorOf = (f: StrategyFamily) => vectorAt(f, anchorFor(f));
  const level = label(LEVEL_LABEL, cmp?.target_impact_level, 'the program’s level');

  // H4: hydrate swaps from the URL once (writers score them; failures name the dropped services).
  useEffect(() => {
    if (hydrated.current || !cmp || !data) return;
    hydrated.current = true;
    const fromUrl = parseSwaps(sp.get('swap'));
    if (urlFamily && Object.keys(fromUrl).length && data.can_write) void runSwap(urlFamily, anchorFor(urlFamily), fromUrl, { fromUrl: true });
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Round-3 H2: a stale decision signed with swaps is re-scored once (keyed by decision) so the column and
  // the rows show the plan Confirm will sign, not the generated one.
  useEffect(() => {
    if (!cmp || !data?.can_write || !decision || !stale || staleRescored.current === decision.decision_id) return;
    const a = decision.anchor ?? null;
    const sw = signedSwaps(decision.family, a);
    staleRescored.current = decision.decision_id;
    if (!Object.keys(sw).length || swaps[keyOf(decision.family, a)]) return;
    setAwaitScore(keyOf(decision.family, a));
    void runSwap(decision.family, a, sw, { onDone: () => setAwaitScore(null), onFail: () => { setAwaitScore(null); setError({ text: 'The signed plan could not be scored against the latest verdicts. Choose again.' }); } });
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const syncUrl = (f: StrategyFamily, anchor: string | null, sw: Record<string, string>) => {
    const n = new URLSearchParams(sp);
    n.set('view', 'strategies'); n.set('family', f);
    if (anchor) n.set('target', anchor); else n.delete('target');
    const enc = formatSwaps(sw);
    if (enc) n.set('swap', enc); else n.delete('swap');
    setSp(n, { replace: true });
  };

  /** Score swaps for ONE explicit candidate (R2-H2: the anchor is passed, never read from state). */
  async function runSwap(f: StrategyFamily, anchor: string | null, nextIn: Record<string, string>, opts: { fromUrl?: boolean; rowId?: string; onFail?: () => void; onDone?: () => void } = {}) {
    const k = keyOf(f, anchor);
    const base = candidateAt(f, anchor);
    if (!base) return;
    // Concurrent row changes: every request carries the latest pending value of every row of this candidate.
    if (opts.rowId) pendingRef.current[k] = { ...(pendingRef.current[k] ?? {}), [opts.rowId]: nextIn[opts.rowId]! };
    const next = { ...nextIn, ...(pendingRef.current[k] ?? {}) };
    const choices = Object.entries(next).filter(([id, o]) => base.instance.choices.find((c) => c.item_id === id)?.option !== o).map(([item_id, option]) => ({ item_id, option }));
    if (!choices.length) {
      setSwaps((s) => ({ ...s, [k]: {} })); setCustoms((c) => { const n = { ...c }; delete n[k]; return n; });
      if (opts.rowId) setPending((p) => { const n = { ...p }; delete n[opts.rowId!]; return n; });
      syncUrl(f, anchor, {});
      setLive(`${fullName(f, anchor)}: back to the generated plan.`);
      opts.onDone?.();
      return;
    }
    const my = ++seq.current;
    setBusy(`swap:${k}`); setError(null);
    try {
      const r = await scoreStrategy(slug, program, { family: f, anchor, choices });
      if (my !== seq.current || !r.comparison.custom) return; // a newer request won
      setSwaps((s) => ({ ...s, [k]: next }));
      setCustoms((c) => ({ ...c, [k]: r.comparison.custom! }));
      syncUrl(f, anchor, next);
      // Clear only the pending values this request carried (a newer one for the same row stays).
      const sent = pendingRef.current[k] ?? {};
      for (const [id, o] of Object.entries(sent)) if (next[id] === o) delete sent[id];
      setPending((p) => { const n = { ...p }; for (const id of Object.keys(next)) if (!(id in sent)) delete n[id]; return n; });
      const v = r.comparison.custom.vector;
      setLive(`${fullName(f, anchor)}: ${fitText(v, level).text}. ${impactLines(v)[0]}.`);
      opts.onDone?.();
    } catch (e) {
      if (my !== seq.current) return;
      if (opts.rowId) { delete (pendingRef.current[k] ?? {})[opts.rowId]; setPending((p) => { const n = { ...p }; delete n[opts.rowId!]; return n; }); }
      if (opts.onFail) { opts.onFail(); return; }
      if (opts.fromUrl) {
        const names = Object.keys(next).map((id) => cmp?.items.find((i) => i.item_id === id)?.name).filter(Boolean).join(', ');
        setError({ text: `The changes in this link could not be scored (${names || 'its services'}). The generated plan is shown.`, retry: () => { setError(null); void runSwap(f, anchor, next, opts); } });
      } else setError({ text: errMsg(e, 'That option could not be scored.') });
    } finally {
      if (my === seq.current) setBusy(null);
    }
  }

  if (q.isLoading) return <Spinner label="Loading the strategies" />;
  // (hooks above; render below)
  if (q.error || !data || !cmp) return <Alert variant="error"><p>The strategies could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const { can_write: canWrite, can_decide: canDecide } = data;
  const items = new Map(cmp.items.map((i) => [i.item_id, i]));
  const catalogEmpty = cmp.catalog_entries === 0;
  const anchor = anchorFor(fam);
  const ck = keyOf(fam, anchor);
  const curSwaps = swapsAt(fam, anchor);
  const shownVector = vectorOf(fam);
  const hasSwaps = Object.keys(curSwaps).length > 0;
  const viewingChosen = isChosen(fam, anchor);
  const chosenNow = viewingChosen && !swaps[ck] && !stale;
  const viewingName = fullName(fam, anchor);
  const scoring = !!busy?.startsWith('swap:') || awaitScore === ck;
  const swappedNames = Object.keys(curSwaps).map((id) => items.get(id)?.name).filter(Boolean) as string[];
  const askSubject = `GAC Pro strategy: ${viewingName}${swappedNames.length ? ` (changed: ${swappedNames.join(', ')})` : ''}`;
  const ask = (verb: string, primary = false) => <AskAdmin slug={slug} compact primary={primary} verb={verb} what="runs the FedRAMP or GovRAMP program" subject={askSubject} />;
  const leaderKey = cmp.leader?.key ?? null;
  const leaderUnknown = cmp.leader ? (group(cmp.leader.family)?.candidates[0]?.vector.verdicts_resolved.unknown_item_ids.length ?? 0) : 0;
  const leaderTag = cmp.leader ? (cmp.leader.status === 'unknown' ? `Leading, ${plural(leaderUnknown, 'answer')} missing` : 'Best fit so far') : null;
  const g = group(fam);
  const rec = g && g.candidates.length > 1 ? g.candidates.find((c) => keyOf(fam, c.instance.anchor) === g.recommended) ?? null : null;
  const notRecommended = !!rec && (rec.instance.anchor ?? null) !== anchor;

  const pick = (f: StrategyFamily) => { setViewing(f); setFilter(null); setError(null); syncUrl(f, anchorFor(f), swapsAt(f, anchorFor(f))); };
  const pickTarget = (f: StrategyFamily, t: string | null) => { setTargets((x) => ({ ...x, [f]: t })); setViewing(f); setFilter(null); syncUrl(f, t, swapsAt(f, t)); };
  const reset = () => {
    setSwaps((s) => ({ ...s, [ck]: {} })); setCustoms((c) => { const n = { ...c }; delete n[ck]; return n; });
    syncUrl(fam, anchor, {}); focusNext.current = 'detail'; setLive(`${viewingName}: back to the generated plan.`);
  };
  const openChoose = (presetReason?: string) => { if (presetReason !== undefined && !rationale) setRationale(presetReason); setChoosing(true); setFieldErr(null); };
  const cancelChoose = () => { setChoosing(false); setFieldErr(null); focusNext.current = 'choose'; };
  const doChoose = async () => {
    if (busyRef.current) return;
    const why = reasonRef.current.trim();
    if (!why) { setFieldErr('Say why this strategy.'); focusNext.current = 'reason'; return; }
    busyRef.current = 'choose';
    setBusy('choose'); setError(null);
    const base = candidate(fam);
    const choices = base ? Object.entries(curSwaps).filter(([id, o]) => base.instance.choices.find((c) => c.item_id === id)?.option !== o).map(([item_id, option]) => ({ item_id, option })) : [];
    try {
      await chooseStrategy(slug, program, { family: fam, anchor, choices, rationale: why });
      setChoosing(false); setRationale(''); setSwaps({}); setCustoms({}); setViewing(fam);
      await qc.invalidateQueries({ queryKey: key });
      await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'strategy-decisions'] });
      await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'verdicts', program] });
      syncUrl(fam, anchor, {});
      focusNext.current = 'chosen';
      show({ message: `${strategyName(fam, anchor)} chosen`, details: `${viewingName} chosen and signed.`, returnFocus: chosenRef.current });
    } catch (x) {
      const b = errBody(x);
      if (x instanceof ApiError && x.status === 422) { setFieldErr(errMsg(x, 'Check the reason.')); reason.current?.focus(); }
      else if (x instanceof ApiError && x.status === 409) {
        setChoosing(false);
        await qc.invalidateQueries({ queryKey: key });
        if (b?.code === 'verdicts_pending' && b.coverage_conflict_item_ids?.length) {
          const first = b.coverage_conflict_item_ids[0]!;
          const name = b.coverage_conflict_item_ids.length === 1 ? cmp.items.find((i) => i.item_id === first)?.name ?? 'a service' : `${b.coverage_conflict_item_ids.length} services`;
          focusNext.current = 'answer';
          setError({ text: `Sources disagree on whether the provider covers ${name}. Say whether it’s covered, or not covered to plan a replacement; then choose.`, coverage: first });
        } else if (b?.code === 'verdicts_pending') { focusNext.current = 'answer'; setError({ text: 'Some services still need answers. Answer them, then choose.' }); }
        else if (b?.code === 'no_items') { focusNext.current = 'heading'; setError({ text: 'Add the services your product depends on first.', inventory: true }); }
        else { focusNext.current = 'detail'; setError({ text: 'The verdicts changed. Review the new scores and choose again.' }); }
      } else if (x instanceof ApiError && x.status === 503) {
        // R2-H3: a platform fault; keep the form and the reason, offer Retry and support.
        setError({ text: 'Signing is unavailable right now. Your choice and reason are kept; try again in a few minutes.', support: true, retry: () => { setError(null); focusNext.current = 'reason'; void doChoose(); } });
      } else setError({ text: errMsg(x, 'The choice could not be saved.') });
    } finally { busyRef.current = null; setBusy(null); }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!rationale.trim()) { setFieldErr('Say why this strategy.'); reason.current?.focus(); return; }
    await doChoose();
  };
  const download = async () => {
    if (!decision || busy) return;
    setBusy('download'); setError(null);
    try {
      const r = await getStrategyDecision(slug, decision.decision_id);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `strategy-decision-${program}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      if (r.verification.valid) show({ message: 'Signed record downloaded', details: 'Signed record downloaded. The signature checks out.' });
      else {
        show({ message: 'Signed record downloaded', details: 'Signed record downloaded. The signature could not be checked right now.' });
        setError({ text: 'The signature could not be checked right now. Try again before you share the record.', tone: 'warning' });
      }
    } catch (e) { setError({ text: errMsg(e, 'The record could not be downloaded.') }); } finally { setBusy(null); }
  };
  const verifyUrl = decision ? `${typeof location !== 'undefined' ? location.origin : ''}/verify/infra-decision/${encodeURIComponent(decision.decision_id)}` : '';
  const copyVerify = async () => {
    try { await navigator.clipboard.writeText(verifyUrl); show({ message: 'Verify link copied', details: 'Verify link copied. Anyone with it can check the signature.' }); }
    catch { setError({ text: `Copy was unavailable. The verify link is ${verifyUrl}`, tone: 'warning' }); }
  };
  const saveEstimate = async (it: ItemOptions, option: string, band: CostBand | null) => {
    setBusy(`est:${it.item_id}`); setError(null);
    try {
      await estimateCost(slug, program, it.item_id, { option, band });
      await qc.invalidateQueries({ queryKey: key });
      setEstimating(null);
      setCustoms({});
      focusNext.current = `est:${it.item_id}`;
      show({ message: band ? 'Estimate saved' : 'Estimate removed', details: band ? `${it.name}: your estimate, ${COST_LABEL[band].toLowerCase()} cost, is used where the catalog has none.` : `${it.name}: your estimate is removed.` });
      // The viewed plan's swaps stay; score them again with the new estimate.
      if (Object.keys(curSwaps).length) void runSwap(fam, anchor, curSwaps);
    } catch (e) { setError({ text: errMsg(e, 'The estimate could not be saved.') }); } finally { setBusy((b) => (b === `est:${it.item_id}` ? null : b)); }
  };
  const closeEstimate = (itemId: string) => { setEstimating(null); focusNext.current = `est:${itemId}`; };

  // ---- the one primary action (head row) ----
  const chooseLabel = catalogEmpty ? 'Choose Stay commercial for now'
    : stale && viewingChosen ? `Confirm ${strategyName(fam, anchor)}`
    : viewingChosen && hasSwaps ? 'Choose with your changes'
    : decision ? `Choose ${strategyName(fam, anchor)} instead` : `Choose ${strategyName(fam, anchor)}`;
  let primary: ReactNode = null;
  // Owner decision 2026-10-09: when only coverage conflicts block the choice, the step is settling them (owner / admin).
  const conflictIds = cmp.coverage_conflict_item_ids ?? [];
  const onlyConflicts = cmp.pending > 0 && conflictIds.length === cmp.pending;
  if (cmp.pending && onlyConflicts) primary = canDecide ? <Button ref={answerBtn} compact variant="primary" onClick={() => onBack({ itemId: conflictIds[0]!, panel: 'coverage' })}>{COVERAGE_ACTION}</Button> : ask('say whether the provider covers it', true);
  else if (cmp.pending) primary = canWrite ? <Button ref={answerBtn} compact variant="primary" onClick={() => onBack()}>Answer them</Button> : ask('answer the open verdicts', true);
  else if (chosenNow && decision) primary = onOpenPlan ? <Button compact variant="primary" onClick={onOpenPlan}>Open the target plan</Button>
    : onBackToScope ? <Button compact variant="primary" onClick={onBackToScope}>{`Back to ${programLabel(program)} Scope`}</Button> : null;
  else if (canDecide) {
    primary = <Button ref={chooseBtn} compact variant="primary" aria-expanded={choosing} aria-controls="sc-choose"
      aria-disabled={(stale && viewingChosen && awaitScore === ck) || undefined}
      onClick={() => {
        if (stale && viewingChosen && awaitScore === ck) return;
        if (catalogEmpty) { if (fam !== 'stay_commercial') pick('stay_commercial'); openChoose('Re-decide when options are reviewed'); }
        else openChoose(stale && viewingChosen ? decision?.rationale : undefined);
      }}>{chooseLabel}</Button>;
  } else primary = ask('choose the strategy', true);

  // ---- matrix ----
  const cols = FAMILIES.map((f) => ({ f, v: vectorOf(f), same: catalogEmpty && f !== 'stay_commercial' }));
  const live1 = cols.filter((c) => !c.same && c.v);
  const valOf = (row: 'open' | 'inh' | 'code', v: StrategyVector): { n: number; unknown: number; total: number } => {
    if (row === 'open') return { n: v.verdicts_resolved.unresolved_item_ids.length, unknown: v.verdicts_resolved.unknown_item_ids.length, total: v.verdicts_resolved.replace.total + v.verdicts_resolved.partition.total };
    if (row === 'inh') return { n: v.inheritance_gained.count, unknown: v.inheritance_gained.unknown_item_ids.length, total: v.inheritance_gained.count + v.inheritance_gained.unknown_item_ids.length };
    return { n: v.code_impact.call_sites, unknown: v.code_impact.unknown_item_ids.length, total: v.code_impact.call_sites + v.code_impact.unknown_item_ids.length };
  };
  /** Every service behind the cell is unknown (else the known part shows, even when it is 0). */
  const fullyAt = (row: 'open' | 'inh' | 'code', v: StrategyVector) => {
    const x = valOf(row, v);
    if (!x.unknown) return false;
    if (row === 'open') return x.unknown >= x.total;
    const moved = v.items.filter((l) => l.kind && l.kind !== 'keep');
    const known = row === 'inh' ? moved.filter((l) => l.inheritance_gained !== null).length : moved.filter((l) => l.call_sites !== null).length;
    return known === 0;
  };
  const rowFullyUnknown = (row: 'open' | 'inh' | 'code') => live1.length > 0 && live1.every((c) => fullyAt(row, c.v!));
  const best: Record<'open' | 'inh' | 'code', boolean[]> = {
    open: bestColumns(cols.map((c) => (c.same || !c.v ? null : valOf('open', c.v))), 'min'),
    inh: bestColumns(cols.map((c) => (c.same || !c.v ? null : valOf('inh', c.v))), 'max'),
    code: bestColumns(cols.map((c) => (c.same || !c.v ? null : valOf('code', c.v))), 'min'),
  };
  const passing = live1.filter((c) => c.v!.class_fit.status === 'pass').length;
  const fitBest = cols.map((c) => !c.same && !!c.v && c.v.class_fit.status === 'pass' && passing >= 1 && passing <= 2 && live1.length > passing);
  const allCostUnknown = live1.length > 0 && live1.every((c) => !c.v!.cost_band.max && c.v!.cost_band.counts.unknown > 0);
  const notKnownLink = (f: StrategyFamily, visible: string, kind: UnknownFilter, n: number) => (
    <button type="button" className="gp-sc-unknown" onClick={() => { pick(f); setFilter(kind); focusNext.current = 'detail'; }} aria-label={`${visible}: ${plural(n, 'service')} where ${FILTER_TEXT[kind]} · ${FAMILY_LABEL[f]}. Show them`}>{visible}</button>
  );
  const numCell = (f: StrategyFamily, v: StrategyVector, row: 'open' | 'inh' | 'code', isBest: boolean, kind: UnknownFilter, word: 'fewest' | 'most') => {
    const x = valOf(row, v);
    if (fullyAt(row, v)) return notKnownLink(f, 'Not known', kind, x.unknown);
    return (
      <span className="gp-sc-val">
        <b data-best={isBest || undefined}>{x.n}</b>
        {x.unknown ? <span className="gp-sc-part">{' + '}{notKnownLink(f, `${x.unknown} not known`, kind, x.unknown)}</span> : null}
        {isBest ? <><span className="gp-sc-best" aria-hidden="true">Best</span><span className="sr-only">{` (${word})`}</span></> : null}
      </span>
    );
  };
  const fitCell = (f: StrategyFamily, v: StrategyVector, isBest: boolean) => {
    const t = fitText(v, level);
    const itemLevel = v.class_fit.reasons.filter((r) => r.item_id).length;
    return (
      <span className="gp-sc-val">
        <button type="button" className="gp-cb-chip gp-sc-fit" data-tone={t.tone} onClick={() => {
          pick(f); setFilter(itemLevel ? 'fit' : null);
          focusNext.current = v.class_fit.reasons.some((r) => WORKSPACE_FIT.has(r.code)) ? 'vvk' : 'detail';
        }}
          aria-label={`${t.text} · ${FAMILY_LABEL[f]}. ${itemLevel ? `Show the ${plural(itemLevel, 'service')} that decide it` : 'Show why'}`}>{t.text}</button>
        {isBest ? <><span className="gp-sc-best" aria-hidden="true">Best</span><span className="sr-only"> (fits)</span></> : null}
      </span>
    );
  };
  const costCell = (f: StrategyFamily, v: StrategyVector) => {
    const max = v.cost_band.max;
    if (!max && v.cost_band.counts.unknown) return notKnownLink(f, 'Not known', 'cost', v.cost_band.counts.unknown);
    return <span className="gp-sc-val">{max ? <b>{`${COST_LABEL[max]}${v.cost_band.estimated ? ' (your estimate)' : ''}`}</b> : <span className="gp-dim">No new cost</span>}
      {max && v.cost_band.counts.unknown ? <span className="gp-sc-part">{' + '}{notKnownLink(f, `${v.cost_band.counts.unknown} not known`, 'cost', v.cost_band.counts.unknown)}</span> : null}</span>;
  };
  const sameCell = <span><span aria-hidden="true">—</span><span className="sr-only">same as Baseline</span></span>;
  const rows: Array<{ id: 'fit' | 'open' | 'inh' | 'code' | 'cost'; label: string }> = [
    { id: 'fit', label: 'Fits the level' }, { id: 'open', label: 'Left open' }, { id: 'inh', label: 'Controls gained' }, { id: 'code', label: 'Code to change' }, { id: 'cost', label: 'Cost' },
  ];
  const hiddenRows = new Set<string>([...(allCostUnknown ? ['cost'] : []), ...(rowFullyUnknown('inh') ? ['inh'] : []), ...(rowFullyUnknown('code') ? ['code'] : [])]);
  const hiddenLines = [
    hiddenRows.has('cost') ? `Cost isn’t known for any option yet${canWrite ? '. Add your estimate on a service below.' : '.'}` : null,
    hiddenRows.has('inh') ? 'Controls gained aren’t known for any option yet.' : null,
    hiddenRows.has('code') ? `Code to change isn’t known for any option yet${canWrite && onOpenInventory ? '. Scan the repository to count it.' : '.'}` : null,
  ].filter(Boolean) as string[];

  // ---- workspace-level fit reasons (R2-H6) ----
  const vvk = shownVector?.class_fit.reasons.filter((r) => WORKSPACE_FIT.has(r.code)) ?? [];
  const below = vvk.filter((r) => r.code === 'VVK_BELOW_MINIMUM').length;
  const topFit = shownVector?.class_fit.reasons[0] ?? null;

  // ---- service rows ----
  const lines = (shownVector?.items ?? []).filter((l) => items.has(l.item_id));
  const rank = (l: Line) => (l.resolves === 'no' ? 0 : l.resolves === 'unknown' ? 1 : 2);
  const sorted = [...lines].sort((a, b) => rank(a) - rank(b) || (items.get(a.item_id)!.name < items.get(b.item_id)!.name ? -1 : 1));
  const filtered = filter && shownVector ? sorted.filter((l) => matches(shownVector, l, filter)) : sorted;
  const openRows = filter ? filtered : filtered.filter((l) => rank(l) < 2);
  const doneRows = filter ? [] : filtered.filter((l) => rank(l) === 2);
  const fitNow = shownVector ? fitText(shownVector, level) : null;

  const rowEl = (line: Line) => {
    const it = items.get(line.item_id)!;
    const optValue = pending[line.item_id] ?? curSwaps[line.item_id] ?? line.option;
    const opt = it.options.find((o) => o.option === optValue);
    const why = itemWhy(line.reasons);
    const acts = itemActions(line.reasons);
    const status = line.resolves === 'yes' ? 'Resolves it' : line.resolves === 'no' ? 'Stays open' : line.resolves === 'unknown' ? 'Not known yet' : '';
    const tone = line.resolves === 'yes' ? 'ok' : line.resolves === 'no' ? 'bad' : line.resolves === 'unknown' ? 'warn' : undefined;
    const swapped = line.item_id in curSwaps;
    const estimate = opt?.estimate ?? null;
    const optText = (o: ItemOptions['options'][number]) => `${o.label}${o.verified === false ? ' · not verified' : ''}${o.estimate ? ` · your estimate: ${COST_LABEL[o.estimate.band].toLowerCase()} cost` : ''}`;
    const actions: ReactNode[] = [];
    if (acts.includes('scan')) actions.push(canWrite && onOpenInventory ? <button key="scan" type="button" className="gp-cb-textbtn" onClick={onOpenInventory}>Scan the repository</button> : <span key="scan">{ask('scan the repository')}</span>);
    if (acts.includes('coverage')) actions.push(canDecide ? <button key="cov" type="button" className="gp-cb-textbtn" onClick={() => onBack({ itemId: line.item_id, panel: 'coverage' })}>Record the coverage</button> : <span key="cov">{ask('record the coverage')}</span>);
    if (acts.includes('target')) {
      const hasTargetRow = (group(fam)?.candidates.length ?? 0) > 1;
      actions.push(canWrite ? <button key="target" type="button" className="gp-cb-textbtn" onClick={() => { focusNext.current = hasTargetRow ? `target:${fam}` : `opt:${line.item_id}`; setLive(hasTargetRow ? 'Pick another target.' : `Pick another option for ${it.name}.`); }}>{hasTargetRow ? 'Pick another target' : 'Pick another option'}</button> : <span key="target">{ask('pick another target')}</span>);
    }
    if (acts.includes('pick')) actions.push(canWrite ? <button key="pick" type="button" className="gp-cb-textbtn" onClick={() => { focusNext.current = `opt:${line.item_id}`; setLive(`Pick another option for ${it.name}.`); }}>Pick another option</button> : <span key="pick">{ask('pick another option')}</span>);
    // One estimate button per row (stable key): its label switches Add / Change, so focus survives a save.
    if ((acts.includes('estimate') || estimate) && line.kind && line.kind !== 'keep') {
      actions.push(canWrite
        ? <button key="est" type="button" className="gp-cb-textbtn" data-est-btn={line.item_id} aria-expanded={estimating === line.item_id} onClick={() => setEstimating(estimating === line.item_id ? null : line.item_id)}>{estimate ? 'Change your estimate' : 'Add your estimate'}</button>
        : <span key="est">{ask('add a cost estimate')}</span>);
    }
    return (
      <li key={line.item_id} className="gp-cb-sg gp-sc-row" data-swapped={swapped || undefined}>
        <span className="gp-cb-main">
          <span className="gp-vb-name">{it.name}</span>
          <span className="gp-cb-meta">
            <span className="gp-cb-chip">{VERDICT_LABEL[line.verdict as keyof typeof VERDICT_LABEL] ?? 'Verdict'}</span>
            {status ? <span className="gp-cb-chip" data-tone={tone}>{status}</span> : null}
            {swapped ? <span className="gp-cb-tag">{viewingChosen && !swaps[ck] ? 'Changed in the chosen plan' : 'Your change'}</span> : null}
            {acts.includes('gacpro') ? <span className="gp-cb-tag">Waiting on GAC Pro review</span> : null}
            {estimate ? <span className="gp-cb-tag">{`Your estimate: ${COST_LABEL[estimate.band].toLowerCase()} cost`}</span> : null}
            {!canWrite ? <span>{opt?.label ?? 'Keep it'}</span> : null}
          </span>
          {why.length ? <details className="gp-cb-why"><summary>Why<span className="sr-only">{` for ${it.name}`}</span></summary><span>{why.join(' · ')}</span></details> : null}
          {actions.length ? <span className="gp-actions gp-sc-rowacts">{actions}</span> : null}
          {estimating === line.item_id ? <EstimateForm itemId={it.item_id} name={it.name} current={estimate?.band ?? null} busy={busy === `est:${it.item_id}`}
            onCancel={() => closeEstimate(it.item_id)} onSave={(b) => void saveEstimate(it, optValue, b)} /> : null}
        </span>
        {canWrite ? (
          <span className="gp-cb-act">
            <Field id={`sc-opt-${line.item_id}`} label={<>Plan<span className="sr-only">{` for ${it.name}`}</span></>}>
              <Select value={optValue} aria-busy={busy === `swap:${ck}` || undefined}
                onChange={(e) => { const v = e.currentTarget.value; setPending((p) => ({ ...p, [line.item_id]: v })); void runSwap(fam, anchor, { ...curSwaps, [line.item_id]: v }, { rowId: line.item_id }); }}>
                {it.options.map((o) => <option key={o.option} value={o.option}>{optText(o)}</option>)}
              </Select>
            </Field>
          </span>
        ) : null}
      </li>
    );
  };

  const targetRow = FAMILIES.some((f) => (group(f)?.candidates.length ?? 0) > 1);

  return (
    <Card>
      <div className="gp-inv gp-vb gp-sc" ref={root}>
        <button type="button" className="gp-cb-back" onClick={() => onBack()}><span aria-hidden="true">‹ </span>Back to verdicts</button>
        <div className="gp-cb-head">
          <h3 className="gp-card-h" id="sc-h" ref={heading} tabIndex={-1}>{`Strategies · ${level}`}</h3>
          <span className="gp-actions">
            {cmp.target_level_source === 'default' ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" to={`${prefix}/programs/${program}`}>Set the level (assumed)</Link> : null}
            {decision ? <Button compact variant="ghost" onClick={() => void copyVerify()}>Copy verify link</Button> : null}
            {decision ? <Button compact variant="ghost" aria-busy={busy === 'download' || undefined} aria-disabled={busy === 'download' || undefined} onClick={() => void download()}>{busy === 'download' ? 'Preparing…' : 'Download signed record'}</Button> : null}
            {primary}
          </span>
        </div>
        <p className="sr-only" role="status">{live}</p>

        {decision ? (
          <div className="gp-cb-callout" data-tone={stale ? 'warn' : 'info'} ref={chosenRef} tabIndex={-1}>
            {stale ? (
              <span><b>{`Chosen ${fmtDate(decision.decided_at)}, before the latest verdicts.`}</b> <span className="gp-dim">{`${fullName(decision.family, decision.anchor)}. Review and confirm or choose again.`}</span></span>
            ) : (
              <span><b>{`Chosen: ${fullName(decision.family, decision.anchor)}`}</b> <span className="gp-dim">{`${fmtDate(decision.decided_at)} by ${decision.decided_by_name ?? 'an admin'} · signed`}</span></span>
            )}
            {stale && cmp.latest_decision_vector ? (
              <details className="gp-cb-why"><summary>As signed</summary>
                <span>{`${fitText(cmp.latest_decision_vector, level).text} · ${impactLines(cmp.latest_decision_vector).join(' · ')}`}</span></details>
            ) : onOpenPlan ? null : <span className="gp-dim">The target plan starts from this choice.</span>}
          </div>
        ) : null}
        {error ? (
          <Alert variant={error.tone ?? 'error'} onDismiss={() => setError(null)}>
            <p>{error.text}</p>
            <span className="gp-actions">
              {error.retry ? <Button compact onClick={error.retry}>Try again</Button> : null}
              {error.support ? <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Strategy signing unavailable · workspace ${slug}`)}`}>Contact GAC Pro support</a> : null}
              {error.inventory && onOpenInventory ? <Button compact onClick={onOpenInventory}>Review the services you depend on</Button> : null}
              {error.coverage && canDecide && !onlyConflicts ? <Button compact onClick={() => onBack({ itemId: error.coverage!, panel: 'coverage' })}>{COVERAGE_ACTION}</Button> : null}
            </span>
          </Alert>
        ) : null}
        {cmp.pending ? (
          <div className="gp-cb-callout" data-tone="warn">
            <span><b>{`${plural(cmp.pending, 'service')} still ${cmp.pending === 1 ? 'needs' : 'need'} answers`}</b> <span className="gp-dim">{conflictIds.length
              ? `${conflictIds.map((id) => cmp.items.find((i) => i.item_id === id)?.name ?? 'A service').join(', ')}: sources disagree on coverage; settle it either way`
              : 'a strategy is chosen once every verdict is decided'}</span></span>
            {conflictIds.length && !onlyConflicts && canDecide ? <button type="button" className="gp-cb-textbtn" onClick={() => onBack({ itemId: conflictIds[0]!, panel: 'coverage' })}>{COVERAGE_ACTION}</button> : null}
          </div>
        ) : null}
        {catalogEmpty ? <Alert variant="info"><p>Options to move services are still in GAC Pro’s review. For now every strategy is the same as staying commercial.</p></Alert> : null}

        {cmp.items.length === 0 ? (
          <EmptyState title="No services to plan yet" description="Every service is out of scope, or none is added yet." action={<Button compact variant="primary" onClick={() => onBack()}>Back to verdicts</Button>} />
        ) : (
          <>
            <div className="gp-sc-wrap" ref={wrap} role="region" aria-labelledby="sc-h" tabIndex={0} data-scrollable={scrollable || undefined}>
              <table className="gp-sc-matrix">
                <thead>
                  <tr>
                    <td className="gp-sc-corner" />
                    {FAMILIES.map((f) => {
                      const on = f === fam;
                      const a = anchorFor(f);
                      return (
                        <th key={f} scope="col" data-on={on || undefined} data-multi={(group(f)?.candidates.length ?? 0) > 1 || undefined} aria-busy={busy === `swap:${keyOf(f, a)}` || undefined}>
                          <button type="button" className="gp-sc-col" aria-pressed={on} onClick={() => pick(f)} aria-describedby={`sc-hint-${f}`}>{FAMILY_LABEL[f]}</button>
                          {a ? <span className="gp-sc-target">{targetName(a)}</span> : null}
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="gp-sc-meta">
                    <td className="gp-sc-corner" />
                    {FAMILIES.map((f) => {
                      const a = anchorFor(f);
                      const c = candidateAt(f, a);
                      return (
                        <td key={f} data-on={f === fam || undefined}>
                          <span className="gp-sc-hint" id={`sc-hint-${f}`}>{FAMILY_HINT[f]}</span>
                          <span className="gp-sc-tags">
                            {f === 'stay_commercial' ? <span className="gp-cb-tag">Baseline</span> : null}
                            {catalogEmpty && f !== 'stay_commercial' ? <span className="gp-cb-tag">Same as Baseline for now</span> : null}
                            {isChosen(f, a) ? <span className="gp-cb-chip" data-tone="info"><span aria-hidden="true">✓ </span>Chosen</span> : null}
                            {c && leaderKey === keyOf(f, c.instance.anchor) && !(catalogEmpty && f !== 'stay_commercial') ? <span className="gp-sc-outline">{leaderTag}</span> : null}
                            {busy === `swap:${keyOf(f, a)}` ? <span className="gp-dim">Scoring…</span> : null}
                          </span>
                          {c && leaderKey === keyOf(f, c.instance.anchor) && cmp.leader && !(catalogEmpty && f !== 'stay_commercial') ? <span className="gp-sc-hint">{leadText(cmp.leader.reason, leaderUnknown)}</span> : null}
                        </td>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {targetRow ? (
                    <tr>
                      <th scope="row">Target</th>
                      {FAMILIES.map((f) => {
                        const gg = group(f);
                        const a = anchorFor(f);
                        if (!gg || gg.candidates.length < 2) return <td key={f} data-on={f === fam || undefined}><span aria-hidden="true">—</span><span className="sr-only">one plan</span></td>;
                        const recKey = gg.recommended;
                        return (
                          <td key={f} data-on={f === fam || undefined}>
                            <Field id={`sc-target-${f}`} label={<span className="sr-only">{`${FAMILY_LABEL[f]}: target`}</span>}>
                              <Select value={a ?? ''} onChange={(e) => pickTarget(f, e.currentTarget.value || null)}>
                                {gg.candidates.map((cand) => {
                                  const ca = cand.instance.anchor ?? null;
                                  const tags = [keyOf(f, ca) === recKey ? 'Recommended' : null, isChosen(f, ca) ? 'Chosen' : null].filter(Boolean).join(', ');
                                  return <option key={ca ?? 'none'} value={ca ?? ''}>{`${targetName(ca)}${tags ? ` (${tags})` : ''}`}</option>;
                                })}
                              </Select>
                            </Field>
                            {keyOf(f, a) === recKey ? (
                              <span className="gp-sc-hint"><span className="gp-cb-tag">Recommended</span>
                                {leaderKey === keyOf(f, a) && cmp.leader && leadText(cmp.leader.reason, leaderUnknown) === leadText(gg.recommended_reasons[0]) ? null : ` ${leadText(gg.recommended_reasons[0])}`}</span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ) : null}
                  {rows.filter((r) => !hiddenRows.has(r.id)).map((r) => (
                    <tr key={r.id}>
                      <th scope="row">{r.label}</th>
                      {cols.map(({ f, v, same }, i) => (
                        <td key={f} data-on={f === fam || undefined}>
                          {same ? sameCell : !v ? <span className="gp-dim">—</span>
                            : r.id === 'fit' ? fitCell(f, v, fitBest[i]!)
                            : r.id === 'cost' ? costCell(f, v)
                            : numCell(f, v, r.id, best[r.id][i]!, r.id === 'open' ? 'resolution' : r.id === 'inh' ? 'inheritance' : 'code', r.id === 'inh' ? 'most' : 'fewest')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hiddenLines.map((l) => <p key={l} className="gp-dim gp-sc-order">{l}</p>)}
            {notRecommended && rec ? (
              <div className="gp-cb-callout" data-tone="info">
                <span><b>{`Recommended: ${targetName(rec.instance.anchor)}.`}</b> <span className="gp-dim">{leadText(g!.recommended_reasons[0])}{shownVector ? (() => { const s = separatingRow(rec.vector, shownVector, level); return s ? ` · ${s}` : ''; })() : ''}</span></span>
                <Button compact onClick={() => pickTarget(fam, rec.instance.anchor ?? null)}>Switch</Button>
              </div>
            ) : null}
            {vvk.length ? (
              <div className="gp-cb-callout" data-tone="warn" ref={vvkRef} tabIndex={-1}>
                <span><b>{below ? `Automated evidence: ${plural(below, 'KSI')} below the class minimum` : vvk.some((r) => r.code === 'VVK_NO_AUTOMATED_EVIDENCE') ? 'Automated evidence: no KSI has automated evidence yet' : 'Automated evidence: the class minimum is not published yet'}</b></span>
                {vvk.every((r) => r.code === 'VVK_RULES_MISSING') ? <span className="gp-cb-tag">Waiting on GAC Pro review</span>
                  : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${program}/prepare`}>Add a check</Link>}
              </div>
            ) : null}

            <section className="gp-sc-detail" aria-labelledby="sc-detail-h">
              <div className="gp-cb-head">
                <h4 className="gp-vb-name" id="sc-detail-h" ref={detailH} tabIndex={-1}>{`${chosenNow ? 'Chosen plan' : viewingName}: service by service`}</h4>
                <span className="gp-actions">
                  {hasSwaps && !chosenNow ? <><span className="gp-cb-tag">Your changes</span><button type="button" className="gp-cb-textbtn" onClick={reset}>Reset</button></> : null}
                  {filter ? <button type="button" className="gp-cb-textbtn" onClick={() => { setFilter(null); focusNext.current = 'detail'; }}>Show all services</button> : null}
                </span>
              </div>
              {filter ? <p className="gp-dim gp-sc-order">{filter === 'fit' ? 'Showing the services that decide the fit.' : `Showing services where ${FILTER_TEXT[filter]}.`}</p> : null}
              <ul className="gp-cb-rows">{openRows.map(rowEl)}</ul>
              {doneRows.length ? (
                <details className="gp-cb-why"><summary>{`${plural(doneRows.length, 'service')} resolved or already fine`}</summary><ul className="gp-cb-rows">{doneRows.map(rowEl)}</ul></details>
              ) : null}
              {!canDecide && canWrite && hasSwaps ? <p className="gp-dim">Your changes go with the link. Only an owner or an admin signs the choice.</p> : null}
            </section>

            {choosing && canDecide ? (
              <form id="sc-choose" className="gp-inline-form gp-inv-panel" data-esc-owner="" onSubmit={(e) => void submit(e)} aria-labelledby="sc-choose-h"
                onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); cancelChoose(); } }}>
                <h4 className="gp-vb-name" id="sc-choose-h">{decision && !(stale && viewingChosen) ? `Replace the ${fullName(decision.family, decision.anchor)} choice from ${fmtDate(decision.decided_at)}?` : stale && viewingChosen ? `Confirm ${viewingName}` : `Choose ${viewingName}`}</h4>
                {decision ? <p className="gp-dim">{`Earlier reason: ${decision.rationale}`}</p> : null}
                {fitNow && shownVector ? (
                  <p className="gp-cb-meta"><span className="gp-cb-chip" data-tone={fitNow.tone}>{fitNow.text}</span>
                    <span>{`${plural(shownVector.verdicts_resolved.unresolved_item_ids.length, 'service')} left open · ${shownVector.verdicts_resolved.unknown_item_ids.length} not known`}</span>
                    {topFit && FIT_WORDS[topFit.code] ? <span>{`Because ${FIT_WORDS[topFit.code]}`}</span> : null}</p>
                ) : null}
                {notRecommended && rec ? <p className="gp-dim">{`Not the recommended option (${targetName(rec.instance.anchor)}).`}</p> : null}
                <Field id="sc-why" label={`Why ${strategyName(fam, anchor)}?`} error={fieldErr ?? undefined}
                  hint={notRecommended ? 'Say why you chose it over the recommended one. Saved with the signed decision.'
                    : shownVector?.class_fit.status === 'fail' ? 'Say why you chose it even though it doesn’t fit yet. Saved with the signed decision.'
                    : shownVector?.class_fit.status === 'unknown' ? 'Say why you chose it even though its fit isn’t known yet. Saved with the signed decision.' : 'Saved with the signed decision'}>
                  <Textarea ref={reason} rows={3} value={rationale} onChange={(e) => { setRationale(e.currentTarget.value); setFieldErr(null); }} />
                </Field>
                <div className="gp-actions">
                  <Button type="submit" compact variant="primary" aria-busy={busy === 'choose' || undefined} aria-disabled={busy === 'choose' || scoring || undefined}>
                    {busy === 'choose' ? 'Signing…' : scoring ? 'Scoring the changes…' : 'Sign and save'}
                  </Button>
                  <Button compact variant="ghost" onClick={cancelChoose}>Cancel</Button>
                </div>
              </form>
            ) : null}

            {decision && decisionsQ.data?.decisions.length ? (
              <details className="gp-cb-why gp-sc-history">
                <summary>History</summary>
                <ul className="gp-cb-rows">
                  {decisionsQ.data.decisions.map((d) => (
                    <li key={d.decision_id} className="gp-cb-sg">
                      <span className="gp-cb-main">
                        <b>{fullName(d.family, d.anchor)}</b>
                        <span className="gp-cb-meta"><span>{`${fmtDate(d.decided_at)} by ${d.decided_by_name ?? 'an admin'}`}</span>{d.superseded_by ? <span className="gp-cb-tag">Replaced</span> : <span className="gp-cb-tag">In force</span>}</span>
                        <span className="gp-dim">{d.rationale}</span>
                      </span>
                      {canDecide && d.superseded_by ? (
                        <span className="gp-cb-act">
                          <Button compact variant="ghost" onClick={() => {
                            // R2-H2: score the earlier swaps against the earlier target, passed explicitly.
                            const a = d.anchor ?? null;
                            setViewing(d.family); setTargets((x) => ({ ...x, [d.family]: a }));
                            const base = candidateAt(d.family, a);
                            const b = new Map((base?.instance.choices ?? []).map((c) => [c.item_id, c.option]));
                            const sw = Object.fromEntries(d.choices.filter((c) => b.get(c.item_id) !== c.option).map((c) => [c.item_id, c.option]));
                            syncUrl(d.family, a, sw);
                            setRationale(d.rationale); setChoosing(true);
                            if (Object.keys(sw).length) {
                              setAwaitScore(keyOf(d.family, a));
                              void runSwap(d.family, a, sw, {
                                onDone: () => setAwaitScore(null),
                                onFail: () => { setAwaitScore(null); setChoosing(false); setError({ text: 'The earlier plan could not be scored. Try Choose this again.' }); focusNext.current = 'heading'; },
                              });
                            }
                          }}>Choose this again</Button>
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        )}
      </div>
      {toast && !toastApi ? <Toast message={toast.message} details={toast.details} version={toast.version} returnFocus={heading.current} onDismiss={() => setToast(null)} /> : null}
    </Card>
  );
}

const BANDS: CostBand[] = ['low', 'medium', 'high', 'very_high'];
/** B2: a person's cost-band estimate for the service's current option (attested; labelled "your estimate"). */
function EstimateForm({ itemId, name, current, busy, onSave, onCancel }: { itemId: string; name: string; current: CostBand | null; busy: boolean; onSave: (b: CostBand | null) => void; onCancel: () => void }) {
  const [band, setBand] = useState<CostBand | ''>(current ?? '');
  const [err, setErr] = useState<string | null>(null);
  const sel = useRef<HTMLSelectElement>(null);
  useEffect(() => { sel.current?.focus(); }, []);
  return (
    <form className="gp-inline-form gp-inv-panel" data-esc-owner="" aria-label={`Your cost estimate · ${name}`}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onCancel(); } }}
      onSubmit={(e) => { e.preventDefault(); if (!band) { setErr('Choose a cost band.'); sel.current?.focus(); return; } onSave(band); }}>
      <Field id={`sc-est-${itemId}`} label="Your cost estimate" hint="Shown as your estimate until GAC Pro reviews a cost" error={err ?? undefined}>
        <Select ref={sel} value={band} onChange={(e) => { setBand(e.currentTarget.value as CostBand); setErr(null); }}>
          <option value="">Choose</option>
          {BANDS.map((b) => <option key={b} value={b}>{COST_LABEL[b]}</option>)}
        </Select>
      </Field>
      <div className="gp-actions">
        <Button type="submit" compact variant="primary" aria-busy={busy || undefined} aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save estimate'}</Button>
        {current ? <Button compact variant="ghost" onClick={() => onSave(null)}>Remove estimate</Button> : null}
        <Button compact variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
